import type { FollowUp, FollowUpStatus } from '../../models/careOSPrototype';
import { chakra } from '@chakra-ui/react';
import { Pressable } from '@ayble-health/dashboard-components';
import { Chip, type ChipVariant } from 'components/ui/chip';
import { CalendarIcon, ClockIcon, ChevronRight } from './icons';

// C_Follow-ups (Figma — Design System 2.1, node 40005215:9032)
// Spec: docs/design/C_follow-ups-design-spec.md

const chipVariantFor: Record<FollowUpStatus, ChipVariant> = {
  Confirmed: 'aqua',
  Unconfirmed: 'neutral',
  Unscheduled: 'canvas',
}

function FollowUpRow({ item }: { item: FollowUp }) {
  const dateColor = item.status === 'Unscheduled' ? 'var(--blue-grey)' : 'var(--seafoam)'
  return (
    <Pressable
      type="button"
      className="hover-bg-feather"
      css={{
        background: '{colors.canvas}',
        border: 'none',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: '{colors.cloud}',
        fontFamily: '{fonts.body}',
        cursor: 'pointer',
      }}
      style={{
        display: 'flex', flexDirection: 'column', gap: 8,
        width: '100%', padding: '12px 16px 8px',
        textAlign: 'left',
      }}
    >
      {/* Header — author + status tag */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, width: '100%' }}>
        <span style={{
          width: 145, flexShrink: 0,
          fontSize: 'var(--text-md-size)', fontWeight: 'var(--text-md-weight)', lineHeight: 'var(--text-md-lh)', letterSpacing: '0.01em',
          color: 'var(--evergreen)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {item.provider}
        </span>
        <Chip variant={chipVariantFor[item.status]}>{item.status}</Chip>
      </div>

      {/* Details — date + (time) + caret */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: dateColor }}>
            <CalendarIcon />
            <span style={{ fontSize: 'var(--text-xs-size)', lineHeight: 'var(--text-xs-lh)' }}>{item.date}</span>
          </span>
          {item.time && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--seafoam)' }}>
              <ClockIcon />
              <span style={{ fontSize: 'var(--text-xs-size)', lineHeight: 'var(--text-xs-lh)' }}>{item.time}</span>
            </span>
          )}
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, color: 'var(--evergreen)' }}>
          <ChevronRight />
        </span>
      </div>
    </Pressable>
  )
}

export function FollowUpsCard({ items }: { items: FollowUp[] }) {
  return (
    <chakra.div
      css={{
        background: '{colors.canvas}',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: '{colors.cloud}',
        borderRadius: '{radii.card}',
        fontFamily: '{fonts.body}',
      }}
      style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
    >
      {/* Header — calendar icon + label */}
      <chakra.div
        css={{ color: '{colors.evergreen}', borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: '{colors.cloud}' }}
        style={{ display: 'flex', alignItems: 'center', gap: 6, height: 40, padding: '8px 16px' }}
      >
        <CalendarIcon />
        <span style={{ fontSize: 'var(--text-cos-btn-sb-size)', fontWeight: 'var(--text-cos-btn-sb-weight)', lineHeight: 1.25, letterSpacing: '0.01em' }}>
          Requested Follow-ups
        </span>
      </chakra.div>

      {items.map((item, i) => (
        <FollowUpRow key={`${item.provider}-${i}`} item={item} />
      ))}
    </chakra.div>
  )
}
